import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { TimerReset, ShieldCheck, AlertTriangle } from 'lucide-react';

const WARN_SECONDS = 300; // show modal in final 5 minutes

function tokenExp() {
  try {
    const t = localStorage.getItem('apex_token');
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp || null;
  } catch { return null; }
}

export default function SessionExpiryGuard() {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const dismissedUntil = useRef(0);

  useEffect(() => {
    const tick = () => {
      const exp = tokenExp();
      if (!exp) { setVisible(false); setSecondsLeft(null); return; }
      const left = Math.floor(exp - Date.now() / 1000);
      setSecondsLeft(left);
      if (left <= WARN_SECONDS && Date.now() > dismissedUntil.current) {
        setVisible(true);
      } else if (left > WARN_SECONDS) {
        // another tab extended the session — hide the modal here too
        setVisible(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!visible || secondsLeft === null) return null;
  const expired = secondsLeft <= 0;
  const mm = String(Math.max(0, Math.floor(secondsLeft / 60))).padStart(2, '0');
  const ss = String(Math.max(0, secondsLeft % 60)).padStart(2, '0');

  const extend = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const headers = code.trim() ? { 'X-StepUp-Code': code.trim() } : {};
      const { data } = await api.post('/auth/refresh', {}, { headers });
      localStorage.setItem('apex_token', data.access_token);
      setVisible(false); setCode('');
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      const msg = (d && (d.message || d)) || 'Refresh failed';
      if (msg === 'session_max_age_exceeded') {
        setErr('Maximum session age (12h) reached — please sign in again');
      } else if (e2?.response?.status === 403) {
        setErr('Enter a fresh 6-digit code from your authenticator');
      } else {
        setErr(String(msg));
      }
    } finally { setBusy(false); }
  };

  const signInAgain = () => {
    localStorage.removeItem('apex_token');
    localStorage.removeItem('apex_user');
    window.location.href = '/login';
  };

  const dismiss = () => {
    dismissedUntil.current = Date.now() + 60_000; // re-warn in 60s
    setVisible(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999,
                  background: 'rgba(0,0,0,0.72)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(4px)' }}
         data-testid="session-expiry-modal">
      <div style={{ width: 380, background: 'var(--bg-panel, #12151c)',
                    border: '1px solid var(--accent-amber, #FFB000)',
                    padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                      color: 'var(--accent-amber, #FFB000)',
                      fontSize: 12, fontWeight: 800,
                      letterSpacing: '0.12em', marginBottom: 10 }}>
          <TimerReset size={16} />
          {expired ? 'SESSION EXPIRED' : 'SESSION EXPIRING'}
        </div>
        {!expired && (
          <div style={{ textAlign: 'center', margin: '10px 0 16px' }}>
            <span style={{ fontSize: 40, fontWeight: 800,
                           fontVariantNumeric: 'tabular-nums',
                           color: secondsLeft <= 60 ? '#ef4444'
                             : 'var(--accent-amber, #FFB000)' }}
                  data-testid="session-countdown">
              {mm}:{ss}
            </span>
            <div style={{ fontSize: 10, color: '#8b93a7', marginTop: 4 }}>
              until this admin session is revoked
            </div>
          </div>
        )}
        {expired ? (
          <>
            <div style={{ fontSize: 11, color: '#8b93a7', marginBottom: 14 }}>
              Your session has ended (Zero-Trust token TTL). Sign in again to continue.
            </div>
            <button onClick={signInAgain} className="btn-sharp btn-primary w-full"
                    data-testid="session-signin-again"
                    style={{ width: '100%', padding: '8px 0' }}>
              SIGN IN AGAIN
            </button>
          </>
        ) : (
          <form onSubmit={extend}>
            <div style={{ fontSize: 10, color: '#8b93a7', marginBottom: 6 }}>
              EXTEND SESSION — FRESH 6-DIGIT AUTHENTICATOR CODE
            </div>
            <input type="text" inputMode="numeric" maxLength={16}
                   placeholder="000000" autoFocus value={code}
                   onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                   data-testid="session-refresh-code-input"
                   className="input-sharp"
                   style={{ width: '100%', textAlign: 'center',
                            letterSpacing: '0.35em', fontSize: 18,
                            marginBottom: 8 }} />
            {err && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center',
                            color: '#ef4444', fontSize: 10, marginBottom: 8 }}
                   data-testid="session-refresh-error">
                <AlertTriangle size={12} /> {err}
              </div>
            )}
            <button type="submit" disabled={busy}
                    data-testid="session-refresh-button"
                    className="btn-sharp btn-primary"
                    style={{ width: '100%', padding: '8px 0',
                             display: 'flex', gap: 6,
                             alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={12} />
              {busy ? 'EXTENDING…' : 'EXTEND SESSION'}
            </button>
            <button type="button" onClick={dismiss}
                    data-testid="session-dismiss-button"
                    style={{ width: '100%', marginTop: 8, background: 'none',
                             border: 'none', color: '#8b93a7', fontSize: 9,
                             letterSpacing: '0.12em', cursor: 'pointer',
                             textDecoration: 'underline' }}>
              REMIND ME IN 60 SECONDS
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
